import { ChangeEvent, useEffect, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { DarkButton } from '@components'
import { useCommentsContext } from '@templates/approval/contexts'
import { ActionType } from '@templates/approval/contexts/commentsContext/types'
import { t } from '@lingui/core/macro'

const DIALOG_MIN_WIDTH = 600
const COMMENT_MAX_CHAR_LENGTH = 2048 // same limit as Google Sheets

export const CommentDialog = () => {
  const { comments, currentCommentDetails, commentDialogOpen, addComment, dispatch } =
    useCommentsContext()
  const [commentInput, setCommentInput] = useState('')
  const editingComment =
    currentCommentDetails !== null && Boolean(comments[currentCommentDetails.cvId])

  useEffect(() => {
    if (!commentDialogOpen) {
      setCommentInput('')
    }
  }, [commentDialogOpen])

  useEffect(() => {
    const existingCellComment =
      editingComment &&
      comments[currentCommentDetails.cvId]?.[currentCommentDetails.fieldId]?.message

    if (existingCellComment) {
      setCommentInput(existingCellComment)
    }
  }, [editingComment])

  return (
    <Dialog onClose={handleClose} open={commentDialogOpen}>
      <DialogTitle>
        <Typography variant="h3">{editingComment ? 'Edit Comment' : 'Add Comment'}</Typography>
      </DialogTitle>
      <DialogContent style={{ minWidth: DIALOG_MIN_WIDTH }}>
        <TextField
          fullWidth
          multiline
          onChange={handleChangeComment}
          placeholder={t`Enter comments on what needs to be corrected...`}
          rows={10}
          maxRows={10}
          value={commentInput}
          variant="outlined"
          inputProps={{ maxLength: COMMENT_MAX_CHAR_LENGTH }}
        />
        <Typography variant="caption">{t`Adding comments will flag this cell as invalid`}</Typography>
      </DialogContent>
      <DialogActions>
        <DarkButton onClick={handleClose}>{t`Cancel`}</DarkButton>
        <DarkButton
          onClick={handleSaveComment}
          disabled={Boolean(!commentInput)}
          contained
          autoFocus
        >
          {t`Save`}
        </DarkButton>
      </DialogActions>
    </Dialog>
  )

  function handleChangeComment(e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
    setCommentInput(e.target.value)
  }

  function handleClose() {
    dispatch({
      type: ActionType.setCommentDialogOpen,
      payload: false,
    })
  }

  function handleSaveComment() {
    if (currentCommentDetails) {
      addComment({
        currentCommentDetails,
        message: commentInput,
      })

      dispatch({
        type: ActionType.setCurrentCommentDetails,
        payload: null,
      })
    }

    dispatch({
      type: ActionType.setCommentDialogOpen,
      payload: false,
    })
  }
}
